import {
	type Capture,
	type CaptureKind,
	markFailed,
	markSending,
	markSent,
} from './captureTypes'

/**
 * The single write path for everything captured in the field.
 *
 * Every field write goes through here — a site, a photo, a note, later a score.
 * It is synchronous today: submit, wait, succeed or fail. The value is the
 * **seam**. When the offline queue arrives in Phase 4 it slots in behind this
 * one function, rather than being threaded through every screen that writes.
 *
 * Handlers are injected rather than imported, which is what lets this be tested
 * with no store, no network and no Redux — and what will let Phase 4 wrap them
 * without rewriting callers.
 */

/** Does the actual work for one kind. Returns whatever the caller wants back. */
export type CaptureHandler<P = never> = (payload: P, capture: Capture) => Promise<unknown>

export type CaptureHandlers = Partial<Record<CaptureKind, CaptureHandler<never>>>

/** Extracted so a thrown string, Error, or API shape all read sensibly. */
const describeError = (error: unknown): string => {
	if (typeof error === 'string' && error.trim()) return error
	if (error instanceof Error && error.message) return error.message
	const payload = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
	if (payload) return payload
	return 'Could not save. It is still here — try again.'
}

/**
 * Submit one capture, returning a new capture carrying the outcome.
 *
 * Never throws. A field app that throws on a failed save loses the thing the
 * user just walked across a car park to record — the failure belongs *on* the
 * capture, where the UI can show it and offer a retry, not on the call stack.
 *
 * **A capture already `sent` is returned untouched.** That is what makes retry
 * safe: retrying a batch cannot resubmit the parts that already landed, which
 * is the same rule `commitImageUpload` uses for its committed ids.
 *
 * Honest limitation: the client-assigned id prevents *this* capture being sent
 * twice. It does not protect against a request that succeeded while its
 * response was lost — the server would create a second record. Chat solves that
 * with a `clientId` the server dedupes on (`chatModel.js`); sites and notes have
 * no equivalent yet. Worth adding before offline replay makes lost responses
 * common.
 */
export const submitCapture = async <C extends Capture>(
	capture: C,
	handlers: CaptureHandlers,
): Promise<C> => {
	if (capture.status === 'sent') return capture

	const handler = handlers[capture.kind] as CaptureHandler<unknown> | undefined
	if (!handler) {
		// A programming error rather than a field failure, but still reported as
		// data: crashing here would lose the capture along with everything else
		// queued behind it.
		return markFailed(capture, `No handler registered for "${capture.kind}" captures.`)
	}

	const sending = markSending(capture)

	try {
		const result = await handler(sending.payload, sending)
		return markSent(sending, result)
	} catch (error) {
		return markFailed(sending, describeError(error))
	}
}

/**
 * Submit several captures, continuing past failures.
 *
 * Sequential, not parallel, and that is deliberate: a site visit produces a
 * handful of photos over a weak cellular connection, where several concurrent
 * uploads are slower than one at a time and far more likely to fail together.
 * `ImageUploadWizard` caps its own concurrency at 3 for the same reason.
 *
 * One failure never stops the rest. Losing four good photos because the second
 * one failed is exactly the behaviour this exists to prevent.
 */
export const submitCaptures = async (
	captures: Capture[],
	handlers: CaptureHandlers,
): Promise<Capture[]> => {
	const results: Capture[] = []
	for (const capture of captures) {
		results.push(await submitCapture(capture, handlers))
	}
	return results
}

/** Did everything land? For deciding whether a screen can close. */
export const allSent = (captures: Capture[]): boolean =>
	captures.every(capture => capture.status === 'sent')

/** The ones worth offering a retry for. */
export const failedOf = (captures: Capture[]): Capture[] =>
	captures.filter(capture => capture.status === 'failed')
